import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import DocumentCanvas from "@/components/DocumentCanvas";
import { RECIPIENT_COLORS, FIELD_LIST, FIELD_TYPES } from "@/lib/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  UploadSimple, FileText, Plus, Trash, Users, PaperPlaneTilt, ArrowLeft, ArrowRight,
  DotsSixVertical, CheckCircle, Spinner, ListNumbers, CaretUp, CaretDown, Stack, EnvelopeSimple, BellRinging, X, Certificate,
} from "@phosphor-icons/react";

const STEPS = ["Prepare", "Recipients", "Place Fields", "Send"];

const Stepper = ({ step, editMode }) => (
  <div className="flex items-center gap-2">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              i < step ? "bg-success text-white" : i === step ? "bg-navy text-white" : "bg-slate-200 text-slate-500"
            }`}
          >
            {i < step ? <CheckCircle size={16} weight="fill" /> : i + 1}
          </div>
          <span className={`text-sm font-semibold hidden md:block ${i === step ? "text-navy" : "text-slate-400"}`}>{editMode && s === "Send" ? "Save" : s}</span>
        </div>
        {i < STEPS.length - 1 && <div className={`w-6 md:w-10 h-px ${i < step ? "bg-success" : "bg-slate-200"}`} />}
      </div>
    ))}
  </div>
);

export default function NewEnvelope() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [recipients, setRecipients] = useState([
    { id: crypto.randomUUID(), name: "", email: "", role: "signer", color: RECIPIENT_COLORS[0] },
  ]);
  const [fields, setFields] = useState([]);
  const [selectedRid, setSelectedRid] = useState(null);
  const [armedField, setArmedField] = useState(null);
  const canvasSectionRef = useRef(null);

  useEffect(() => {
    if (armedField && canvasSectionRef.current) {
      canvasSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [armedField]);

  useEffect(() => {
    if (user?.email) setSenderEmail((s) => s || user.email);
  }, [user]);
  const [signingOrder, setSigningOrder] = useState("parallel");
  const [autoRemind, setAutoRemind] = useState(true);
  const [remindDays, setRemindDays] = useState(3);
  const [includeCert, setIncludeCert] = useState(true);
  const [senderEmail, setSenderEmail] = useState("");
  const [showTplDialog, setShowTplDialog] = useState(false);
  const [tplName, setTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [optField, setOptField] = useState(null);
  const [optValues, setOptValues] = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get("edit") === "1" && !!searchParams.get("template");
  const templateLoaded = useRef(false);
  const [loadedTpl, setLoadedTpl] = useState(null); // { id, name } when started from a template

  const signers = recipients.filter((r) => r.role === "signer");
  const selectedRecipient = recipients.find((r) => r.id === selectedRid) || signers[0];

  // Preload from a saved template (?template=id) — run once only
  useEffect(() => {
    const tid = searchParams.get("template");
    if (!tid || templateLoaded.current) return;
    templateLoaded.current = true;
    (async () => {
      try {
        const t = (await api.get(`/templates/${tid}`)).data;
        const d = (await api.get(`/documents/${t.document_id}`)).data;
        const recs = t.recipients.map((r, i) => ({
          id: crypto.randomUUID(), name: r.name || "", email: r.email || "", role: r.role,
          color: r.color || RECIPIENT_COLORS[i % RECIPIENT_COLORS.length],
        }));
        const flds = t.fields.map((f) => ({
          id: crypto.randomUUID(), recipient_id: recs[f.recipient_index]?.id || recs[0].id,
          type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, required: f.required,
          ...(f.options ? { options: [...f.options] } : {}),
        }));
        setDoc(d);
        setTitle(t.title || t.name);
        setRecipients(recs.length ? recs : recipients);
        setFields(flds);
        setSelectedRid(recs[0]?.id);
        if (t.signing_order) setSigningOrder(t.signing_order);
        if (t.auto_remind !== undefined) setAutoRemind(t.auto_remind);
        if (t.remind_interval_days) setRemindDays([1, 3, 7].reduce((a, b) => (Math.abs(b - t.remind_interval_days) < Math.abs(a - t.remind_interval_days) ? b : a)));
        if (t.include_certificate !== undefined) setIncludeCert(t.include_certificate);
        setLoadedTpl({ id: t.id, name: t.name });
        setTplName(t.name);
        setStep(0);
        toast.success(`Loaded template "${t.name}"`);
      } catch {
        toast.error("Could not load template");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(r.data);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
      toast.success("Document ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addRecipient = () => {
    setRecipients((rs) => [
      ...rs,
      { id: crypto.randomUUID(), name: "", email: "", role: "signer", color: RECIPIENT_COLORS[rs.length % RECIPIENT_COLORS.length] },
    ]);
  };
  const updateRecipient = (id, patch) => setRecipients((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRecipient = (id) => {
    setRecipients((rs) => rs.filter((r) => r.id !== id));
    setFields((fs) => fs.filter((f) => f.recipient_id !== id));
  };
  const moveRecipient = (id, dir) => setRecipients((rs) => {
    const i = rs.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rs.length) return rs;
    const copy = [...rs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const saveTemplate = async (mode = "new") => {
    if (!tplName.trim()) { toast.error("Name your template"); return; }
    setSavingTpl(mode);
    try {
      const body = {
        name: tplName.trim(),
        document_id: doc.id,
        title: title.trim(),
        signing_order: signingOrder,
        auto_remind: autoRemind,
        remind_interval_days: remindDays,
        include_certificate: includeCert,
        recipients: recipients.map((r) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role, color: r.color })),
        fields: fields.map((f) => ({
          recipient_index: recipients.findIndex((r) => r.id === f.recipient_id),
          type: f.type, page: f.page, x: f.x, y: f.y, w: f.w, h: f.h, required: f.required,
          ...(f.options ? { options: [...f.options] } : {}),
        })),
      };
      if (mode === "update") {
        await api.put(`/templates/${loadedTpl.id}`, body);
        setLoadedTpl({ id: loadedTpl.id, name: body.name });
        toast.success(`Template "${body.name}" updated`);
      } else {
        const r = await api.post("/templates", body);
        setLoadedTpl({ id: r.data.id, name: body.name });
        toast.success("Saved as a new template");
      }
      setShowTplDialog(false);
      if (editMode) navigate("/");
      setTplName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save template");
    } finally {
      setSavingTpl(false);
    }
  };

  const addField = useCallback((page, x, y, type, w, h) => {
    if (!selectedRecipient || selectedRecipient.role !== "signer") {
      toast.error("Select a signer to place fields");
      return;
    }
    const cfg = FIELD_TYPES[type];
    setFields((fs) => [...fs, {
      id: crypto.randomUUID(), recipient_id: selectedRecipient.id, type, page, x, y,
      w: w || cfg.w, h: h || cfg.h, required: true,
      ...(cfg.options ? { options: [...cfg.options] } : {}),
    }]);
  }, [selectedRecipient]);

  const moveField = useCallback((id, x, y) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, x, y } : f))), []);
  const resizeField = useCallback((id, w, h) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, w, h } : f))), []);
  const toggleRequired = useCallback((id) => setFields((fs) => fs.map((f) => (f.id === id ? { ...f, required: !f.required } : f))), []);
  const duplicateField = useCallback((id) => setFields((fs) => {
    const src = fs.find((f) => f.id === id);
    if (!src) return fs;
    const x = Math.min(Math.max(src.x + 0.03, 0), 1 - src.w);
    const y = Math.min(Math.max(src.y + 0.03, 0), 1 - src.h);
    return [...fs, {
      ...src, id: crypto.randomUUID(), x, y,
      ...(src.options ? { options: [...src.options] } : {}),
    }];
  }), []);
  const configField = useCallback((field) => {
    setOptField(field);
    setOptValues(field.options && field.options.length ? [...field.options] : [""]);
  }, []);

  const MAX_OPTIONS = 8;
  const updateOpt = (i, val) => setOptValues((vs) => vs.map((v, idx) => (idx === i ? val : v)));
  const addOpt = () => setOptValues((vs) => (vs.length >= MAX_OPTIONS ? vs : [...vs, ""]));
  const removeOpt = (i) => setOptValues((vs) => (vs.length <= 1 ? vs : vs.filter((_, idx) => idx !== i)));
  const moveOpt = (i, dir) => setOptValues((vs) => {
    const j = i + dir;
    if (j < 0 || j >= vs.length) return vs;
    const next = [...vs];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const saveOpts = () => {
    const options = optValues.map((s) => s.trim()).filter(Boolean);
    if (!options.length) { toast.error("Add at least one option"); return; }
    setFields((fs) => fs.map((f) => {
      if (f.id !== optField.id) return f;
      // Grow height so every option is comfortably visible (never shrink below what the sender set).
      const minH = Math.min(0.6, 0.03 + options.length * 0.032);
      return { ...f, options, h: Math.max(f.h, minH) };
    }));
    setOptField(null);
  };
  const removeField = useCallback((id) => setFields((fs) => fs.filter((f) => f.id !== id)), []);

  const canNext = useMemo(() => {
    if (step === 0) return !!doc && title.trim().length > 0;
    if (step === 1) return signers.length > 0 && recipients.every((r) => editMode
      ? (!r.email.trim() || /\S+@\S+\.\S+/.test(r.email))
      : r.name.trim() && /\S+@\S+\.\S+/.test(r.email));
    if (step === 2) return fields.length > 0;
    return true;
  }, [step, doc, title, recipients, signers, fields, editMode]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/envelopes", {
        title: title.trim(),
        document_id: doc.id,
        recipients: recipients.map((r, idx) => ({ id: r.id, name: r.name.trim(), email: r.email.trim(), role: r.role, color: r.color, order: idx })),
        fields: fields.map(({ id, recipient_id, type, page, x, y, w, h, required, options }) => ({ id, recipient_id, type, page, x, y, w, h, required, options })),
        signing_order: signingOrder,
        auto_remind: autoRemind,
        remind_interval_days: remindDays,
        include_certificate: includeCert,
        sender_email: senderEmail.trim() || null,
      });
      toast.success("Document sent");
      navigate(`/envelope/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
      setSending(false);
    }
  };

  const goNext = () => {
    if (!canNext) {
      const msgs = ["Upload a document and add a title", "Fill in each recipient's name and a valid email", "Place at least one field on the document"];
      toast.error(msgs[step]);
      return;
    }
    if (step === 1 && !selectedRid) setSelectedRid(signers[0]?.id);
    if (step === 3) return editMode ? saveTemplate("update") : send();
    setStep((s) => s + 1);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden camino-dark">
      <TopNav>
        <div className="hidden sm:block"><Stepper step={step} editMode={editMode} /></div>
      </TopNav>

      <div className="sm:hidden px-4 py-3 border-b border-slate-200 bg-white overflow-x-auto"><Stepper step={step} editMode={editMode} /></div>

      <main className={`flex-1 min-h-0 flex flex-col ${step === 2 ? "overflow-hidden" : "overflow-y-auto"}`}>
        {/* STEP 0: PREPARE */}
        {step === 0 && (
          <div className="mx-auto max-w-2xl w-full px-6 py-12 animate-fade-up">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-action">Step 1 — Prepare</p>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-navy leading-none">Upload your document</h1>
            <p className="mt-3 text-slate-500 leading-relaxed">PDF or Word (.docx). We convert Word to PDF automatically.</p>

            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              className="mt-8 block cursor-pointer border-2 border-dashed border-slate-300 bg-white p-12 text-center transition-colors hover:border-action hover:bg-action/[0.03]"
              data-testid="upload-document-zone"
            >
              <input type="file" accept=".pdf,.docx,.txt" className="hidden"
                onChange={(e) => handleFile(e.target.files[0])} data-testid="file-input" />
              {uploading ? (
                <div className="flex flex-col items-center text-slate-500">
                  <Spinner size={40} className="animate-spin text-action" />
                  <p className="mt-4 font-semibold">Processing document…</p>
                </div>
              ) : doc ? (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl bg-success/10 flex items-center justify-center">
                    <FileText size={30} weight="duotone" className="text-success" />
                  </div>
                  <p className="mt-4 font-semibold text-navy">{doc.original_filename}</p>
                  <p className="text-sm text-slate-400">{doc.page_count} page{doc.page_count !== 1 ? "s" : ""} · Click to replace</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center">
                    <UploadSimple size={28} weight="bold" color="#fff" />
                  </div>
                  <p className="mt-4 font-heading text-lg font-bold text-navy">Drag &amp; Drop</p>
                  <p className="text-sm text-slate-400">or click to browse</p>
                </div>
              )}
            </label>

            {doc && (
              <div className="mt-8">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Vacation Rental Agreement"
                  className="mt-2 h-12" data-testid="envelope-title-input" />
              </div>
            )}
          </div>
        )}

        {/* STEP 1: RECIPIENTS */}
        {step === 1 && (
          <div className="mx-auto max-w-2xl w-full px-6 py-12 animate-fade-up">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-action">Step 2 — Recipients</p>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-navy leading-none">Who needs to sign?</h1>
            <p className="mt-3 text-slate-500 leading-relaxed">Add each person's name and email. Set signers, or "copy" for people who only receive a finished copy.</p>

            <div className="mt-6 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-navy/[0.06] flex items-center justify-center">
                  <ListNumbers size={20} weight="duotone" className="text-navy" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-navy">Sign in order</div>
                  <div className="text-xs text-slate-400">{signingOrder === "sequential" ? "Recipients sign one after another, top to bottom" : "Everyone can sign at the same time"}</div>
                </div>
              </div>
              <Switch checked={signingOrder === "sequential"} onCheckedChange={(v) => setSigningOrder(v ? "sequential" : "parallel")} data-testid="signing-order-toggle" />
            </div>

            <div className="mt-4 space-y-3">
              {recipients.map((r, i) => (
                <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`recipient-card-${i}`}>
                  <div className="flex items-center gap-3">
                    {signingOrder === "sequential" ? (
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <button onClick={() => moveRecipient(r.id, -1)} disabled={i === 0} className="text-slate-300 hover:text-navy disabled:opacity-30" data-testid={`move-up-${i}`}><CaretUp size={14} weight="bold" /></button>
                        <span className="w-6 h-6 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center" data-testid={`order-badge-${i}`}>{i + 1}</span>
                        <button onClick={() => moveRecipient(r.id, 1)} disabled={i === recipients.length - 1} className="text-slate-300 hover:text-navy disabled:opacity-30" data-testid={`move-down-${i}`}><CaretDown size={14} weight="bold" /></button>
                      </div>
                    ) : (
                      <span className="w-3 h-8 rounded-full shrink-0" style={{ background: r.color }} />
                    )}
                    <div className="grid sm:grid-cols-2 gap-3 flex-1">
                      <Input value={r.name} onChange={(e) => updateRecipient(r.id, { name: e.target.value })}
                        placeholder="Full name" data-testid={`recipient-name-${i}`} />
                      <Input value={r.email} type="email" onChange={(e) => updateRecipient(r.id, { email: e.target.value })}
                        placeholder="email@example.com" data-testid={`recipient-email-${i}`} />
                    </div>
                    {recipients.length > 1 && (
                      <button onClick={() => removeRecipient(r.id)} className="text-slate-300 hover:text-destructive transition-colors" data-testid={`remove-recipient-${i}`}>
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2 pl-6">
                    {["signer", "copy"].map((role) => (
                      <button key={role} onClick={() => updateRecipient(r.id, { role })}
                        data-testid={`recipient-role-${i}-${role}`}
                        className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                          r.role === role ? "bg-navy text-white border-navy" : "bg-white text-slate-500 border-slate-200 hover:border-navy/40"
                        }`}>
                        {role === "signer" ? "Needs to sign" : "Receives copy"}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Button variant="outline" onClick={addRecipient} className="mt-4 rounded-full gap-2 border-dashed" data-testid="add-recipient-btn">
              <Plus size={16} weight="bold" /> Add recipient
            </Button>
          </div>
        )}

        {/* STEP 2: PLACE FIELDS */}
        {step === 2 && (
          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
            <aside className={`w-full md:w-[300px] shrink-0 ${paletteOpen ? "max-h-[40vh]" : "max-h-none"} md:max-h-none md:h-full border-b md:border-b-0 md:border-r border-slate-200 bg-white p-4 md:p-5 overflow-y-auto thin-scroll`}>
              <button
                type="button"
                onClick={() => setPaletteOpen((o) => !o)}
                data-testid="toggle-palette-btn"
                className="md:hidden -m-4 mb-0 p-4 w-[calc(100%+2rem)] flex items-center justify-between text-navy"
              >
                <span className="text-sm font-semibold">
                  Fields{fields.length ? ` · ${fields.length} placed` : ""}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  {paletteOpen ? "Hide" : "Show"}
                  {paletteOpen ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                </span>
              </button>
              <div className={paletteOpen ? "" : "hidden md:block"}>
              <p className="hidden md:block text-[11px] font-semibold uppercase tracking-[0.25em] text-action">Step 3 — Place Fields</p>
              <h2 className="hidden md:block mt-1 font-heading text-xl font-bold tracking-tight text-navy">Assign &amp; place</h2>

              <div className="md:mt-5">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Placing fields for</Label>
                <div className="mt-2 space-y-1.5">
                  {signers.map((r) => (
                    <button key={r.id} onClick={() => setSelectedRid(r.id)} data-testid={`select-signer-${r.id}`}
                      className={`w-full flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                        selectedRecipient?.id === r.id ? "border-navy bg-navy/[0.04]" : "border-slate-200 hover:border-navy/30"
                      }`}>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color }} />
                      <span className="text-sm font-medium text-navy truncate">{r.name || "Unnamed signer"}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 md:mt-6">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tap or drag onto document</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {FIELD_LIST.map((f) => {
                    const Icon = f.icon;
                    const color = selectedRecipient?.color || "#0052FF";
                    const armed = armedField === f.key;
                    return (
                      <button type="button" key={f.key} draggable
                        onDragStart={(e) => { e.dataTransfer.setData("field-type", f.key); setArmedField(null); }}
                        onClick={() => setArmedField((a) => (a === f.key ? null : f.key))}
                        data-testid={`drag-tag-${f.key}`}
                        className={`tag-drag select-none flex items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${armed ? "border-navy ring-2 ring-navy/30" : "border-slate-200"}`}
                        style={{ borderLeft: `4px solid ${color}` }}>
                        <Icon size={16} weight="bold" style={{ color }} />
                        <span className="text-xs font-semibold text-navy">{f.label}</span>
                      </button>
                    );
                  })}
                </div>
                {armedField && (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-navy px-3 py-2 text-xs font-semibold text-white" data-testid="armed-banner">
                    <span className="truncate">Tap the document to place {FIELD_TYPES[armedField].label}</span>
                    <button onClick={() => setArmedField(null)} className="underline shrink-0" data-testid="cancel-armed-btn">Cancel</button>
                  </div>
                )}
                <div className="hidden md:flex mt-4 items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500 leading-relaxed">
                  <DotsSixVertical size={16} className="shrink-0 mt-0.5" />
                  Pick a field, then click to drop a default size — or drag across the document to draw a custom-sized box. Drag placed fields to move; hover to delete, duplicate, or toggle required (∗).
                </div>
                <p className="mt-3 text-xs text-slate-400" data-testid="field-count">{fields.length} field{fields.length !== 1 ? "s" : ""} placed</p>
              </div>
              </div>
            </aside>

            <section ref={canvasSectionRef} className="flex-1 min-h-0 grid-canvas overflow-y-auto thin-scroll p-3 sm:p-6 md:p-10">
              <div className="mx-auto" style={{ maxWidth: 820 }}>
                <DocumentCanvas
                  docId={doc.id} pages={doc.pages} fields={fields} recipients={recipients}
                  mode="editor" selectedRecipientId={selectedRecipient?.id}
                  onDropField={addField} onMoveField={moveField} onRemoveField={removeField}
                  onResizeField={resizeField} onConfigField={configField} onDuplicateField={duplicateField} onToggleRequired={toggleRequired}
                  armedType={armedField} onArmedPlaced={() => setArmedField(null)}
                />
              </div>
            </section>
          </div>
        )}

        {/* STEP 3: REVIEW & SEND */}
        {step === 3 && (
          <div className="mx-auto max-w-2xl w-full px-6 py-12 animate-fade-up">
            <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-action">{editMode ? "Step 4 — Save Template" : "Step 4 — Send & Track"}</p>
            <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-navy leading-none">{editMode ? "Review & save changes" : "Review & send"}</h1>
            <p className="mt-3 text-slate-500 leading-relaxed">
              {editMode
                ? "Check the details below. Saving updates the template for next time. Nothing is sent to anyone."
                : signingOrder === "sequential"
                ? "Recipients will be notified to sign one after another, in the order you set."
                : "Once sent, each signer gets a private signing link you can copy and share."}
            </p>

            <div className="mt-8 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              <div className="p-5 flex items-center gap-3">
                <FileText size={22} weight="duotone" className="text-navy" />
                <div><div className="font-semibold text-navy">{title}</div><div className="text-sm text-slate-400">{doc.original_filename} · {doc.page_count} pages</div></div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3"><Users size={18} weight="duotone" className="text-navy" /><span className="text-sm font-semibold text-navy">{recipients.length} recipients</span></div>
                <div className="space-y-2">
                  {recipients.map((r) => {
                    const count = fields.filter((f) => f.recipient_id === r.id).length;
                    return (
                      <div key={r.id} className="flex items-center gap-2.5 text-sm">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                        <span className="font-medium text-navy">{r.name}</span>
                        <span className="text-slate-400">{r.email}</span>
                        <span className="ml-auto text-xs text-slate-400">{r.role === "signer" ? `${count} field${count !== 1 ? "s" : ""}` : "Copy only"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-5 flex items-center gap-3">
                <ListNumbers size={20} weight="duotone" className="text-navy" />
                <div className="text-sm">
                  <span className="font-semibold text-navy">{signingOrder === "sequential" ? "Sequential signing" : "Everyone at once"}</span>
                  <span className="text-slate-400"> · {signingOrder === "sequential" ? "one after another" : "parallel"}</span>
                </div>
              </div>
            </div>

            {!editMode && (<>
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-navy/[0.04] border border-navy/10 p-4 text-sm text-slate-600">
              <EnvelopeSimple size={18} weight="duotone" className="text-navy shrink-0" />
              After sending, use <span className="font-semibold text-navy">Copy signing link</span> on the tracking page to share with each signer.
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your email (for alerts)</Label>
              <Input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="you@caminoproperties.com" className="mt-2 h-11" data-testid="sender-email-input" />
              <p className="mt-2 text-xs text-slate-400">Prefilled from your account — we'll email you when the document is fully signed or if someone declines.</p>
            </div>
            </>)}

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-action/10 flex items-center justify-center">
                    <BellRinging size={20} weight="duotone" className="text-action" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-navy">Automatic reminders</div>
                    <div className="text-xs text-slate-400">Nudge pending signers until they sign</div>
                  </div>
                </div>
                <Switch checked={autoRemind} onCheckedChange={setAutoRemind} data-testid="auto-remind-toggle" />
              </div>
              {autoRemind && (
                <div className="mt-4 flex items-center gap-2 pl-12">
                  <span className="text-sm text-slate-500">Remind every</span>
                  {[1, 3, 7].map((d) => (
                    <button key={d} onClick={() => setRemindDays(d)} data-testid={`remind-days-${d}`}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${
                        remindDays === d ? "bg-navy text-white border-navy" : "bg-white text-slate-500 border-slate-200 hover:border-navy/40"
                      }`}>
                      {d} day{d !== 1 ? "s" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-action/10 flex items-center justify-center">
                    <Certificate size={20} weight="duotone" className="text-action" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-navy">Attach audit certificate</div>
                    <div className="text-xs text-slate-400">Adds the audit trail pages to the end of the signed copy signers receive</div>
                  </div>
                </div>
                <Switch checked={includeCert} onCheckedChange={setIncludeCert} data-testid="include-certificate-toggle" />
              </div>
            </div>

            {editMode ? (
              <div className="mt-4 rounded-xl border border-action/30 bg-action/[0.04] p-5" data-testid="edit-template-name-card">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Template name</Label>
                <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" className="mt-2 h-11 bg-white" data-testid="edit-template-name-input" />
                <p className="mt-2 text-xs text-slate-500">Saving updates this template only. Nothing is sent.</p>
              </div>
            ) : (
              <Button variant="outline" onClick={() => { setTplName(loadedTpl?.name || title); setShowTplDialog(true); }} className="mt-4 rounded-full gap-2 border-dashed" data-testid="save-template-btn">
                <Stack size={16} weight="duotone" /> Save as template
              </Button>
            )}
          </div>
        )}
      </main>

      <Dialog open={showTplDialog} onOpenChange={setShowTplDialog}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <Stack size={22} weight="duotone" className="text-action" /> Save as template
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Saves the document, title, recipients (names and emails), fields and send settings so you can send new copies in seconds.
            {loadedTpl && <> You started from <span className="font-semibold text-navy">"{loadedTpl.name}"</span> — update it, or leave it unchanged and save a new copy.</>}
          </p>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" className="mt-2 h-11" data-testid="template-name-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTplDialog(false)}>Cancel</Button>
            {loadedTpl && (
              <Button variant="outline" onClick={() => saveTemplate("update")} disabled={!!savingTpl} className="rounded-full gap-2 text-navy" data-testid="update-template-btn">
                {savingTpl === "update" ? <Spinner size={16} className="animate-spin" /> : <Stack size={16} weight="bold" />} Update template
              </Button>
            )}
            <Button onClick={() => saveTemplate("new")} disabled={!!savingTpl} className="rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="confirm-save-template-btn">
              {savingTpl === "new" ? <Spinner size={16} className="animate-spin" /> : <Stack size={16} weight="bold" />} {loadedTpl ? "Save as new template" : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!optField} onOpenChange={(o) => !o && setOptField(null)}>
        <DialogContent data-testid="options-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <CaretDown size={20} weight="bold" className="text-action" />
              {optField?.type === "radio" ? "Radio options" : "Dropdown options"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Type each choice the signer can pick from. Add up to {MAX_OPTIONS}. Drag the field afterwards to size it so every option is easy to read.
          </p>
          <div className="space-y-2 mt-1 max-h-[46vh] overflow-auto pr-1">
            {optValues.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-6 text-center text-xs font-mono text-slate-400">{i + 1}</span>
                <Input
                  value={v}
                  onChange={(e) => updateOpt(i, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                  className="h-10 flex-1"
                  data-testid={`option-input-${i}`}
                  autoFocus={i === optValues.length - 1}
                />
                <div className="flex flex-col -space-y-1">
                  <button
                    type="button"
                    onClick={() => moveOpt(i, -1)}
                    disabled={i === 0}
                    data-testid={`move-option-up-${i}`}
                    className="text-slate-400 hover:text-navy disabled:opacity-25 disabled:hover:text-slate-400 transition-colors"
                    title="Move up"
                  >
                    <CaretUp size={14} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveOpt(i, 1)}
                    disabled={i === optValues.length - 1}
                    data-testid={`move-option-down-${i}`}
                    className="text-slate-400 hover:text-navy disabled:opacity-25 disabled:hover:text-slate-400 transition-colors"
                    title="Move down"
                  >
                    <CaretDown size={14} weight="bold" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeOpt(i)}
                  disabled={optValues.length <= 1}
                  data-testid={`remove-option-${i}`}
                  className="p-2 text-slate-400 hover:text-destructive disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
                  title="Remove option"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={addOpt}
            disabled={optValues.length >= MAX_OPTIONS}
            data-testid="add-option-btn"
            className="mt-1 w-full rounded-full border-dashed gap-2 text-navy"
          >
            <Plus size={16} weight="bold" /> {optValues.length >= MAX_OPTIONS ? `Maximum ${MAX_OPTIONS} options` : "Add option"}
          </Button>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOptField(null)}>Cancel</Button>
            <Button onClick={saveOpts} className="rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="save-options-btn">
              <CheckCircle size={16} weight="bold" /> Save options
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Footer nav */}
      <footer className="flex-none glass border-t border-slate-200/70">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => (step === 0 ? navigate("/") : setStep((s) => s - 1))} className="gap-2 text-slate-500" data-testid="wizard-back-btn">
            <ArrowLeft size={16} /> {step === 0 ? "Cancel" : "Back"}
          </Button>
          <Button onClick={goNext} disabled={sending || !!savingTpl}
            className={`rounded-full gap-2 h-11 px-6 ${step === 3 ? "bg-action hover:bg-action/90" : "bg-navy hover:bg-navy/90"}`}
            data-testid="wizard-next-btn">
            {step === 3 && editMode
              ? (savingTpl ? <><Spinner size={18} className="animate-spin" /> Saving…</> : <><CheckCircle size={18} weight="bold" /> Save changes</>)
              : step === 3 ? (sending ? <><Spinner size={18} className="animate-spin" /> Sending…</> : <><PaperPlaneTilt size={18} weight="bold" /> Send Document</>) : <>Continue <ArrowRight size={16} /></>}
          </Button>
        </div>
      </footer>
    </div>
  );
}
